// SmartSearch — natural-language search dropdown for CalcGlobe.
// Renders a controlled input with:
//   • up to 6 ranked results from /lib/smartSearch.js
//   • full keyboard nav (↑ ↓ Enter Esc)
//   • rotating placeholder hints
//   • light + dark theme support via Tailwind tokens
//
// Used on the homepage hero and the sidebar.

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowRight, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { smartSearch } from '@/lib/smartSearch';
import { PLACEHOLDER_HINTS } from '@/data/searchDictionary';

function usePlaceholderRotator(active) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => setIdx(i => (i + 1) % PLACEHOLDER_HINTS.length), 2800);
    return () => clearInterval(id);
  }, [active]);
  return `Search calculators… try "${PLACEHOLDER_HINTS[idx]}"`;
}

export default function SmartSearch({
  variant = 'hero',                    // 'hero' (dark glass) | 'plain' (theme-aware)
  autoFocus = false,
  testidPrefix = 'smart-search',
  onResultSelected,                    // optional callback after navigation
}) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const focused = open || q.length > 0;
  const placeholder = usePlaceholderRotator(!focused);

  const results = useMemo(() => smartSearch(q, 6), [q]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // Reset selection when results change
  useEffect(() => { setActiveIdx(0); }, [results.length, q]);

  const go = useCallback((r) => {
    if (!r) return;
    setQ(''); setOpen(false);
    // Blur to close mobile keyboard
    inputRef.current?.blur();
    navigate(r.url);
    onResultSelected?.(r);
  }, [navigate, onResultSelected]);

  // Public helper — fire a fixed query as if the user typed it + pressed Enter
  // (used by the QuickChips component)
  const fireQuery = useCallback((text) => {
    setQ(text);
    setOpen(true);
    const list = smartSearch(text, 6);
    if (list.length) go(list[0]);
  }, [go]);

  // Expose fireQuery via a window event (so chips outside the component can trigger it)
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.query) fireQuery(e.detail.query);
    };
    window.addEventListener('calcglobe:fire-search', handler);
    return () => window.removeEventListener('calcglobe:fire-search', handler);
  }, [fireQuery]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); setOpen(true); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (results[activeIdx]) go(results[activeIdx]); }
    else if (e.key === 'Escape') { e.preventDefault(); setQ(''); setOpen(false); inputRef.current?.blur(); }
  };

  const showDropdown = open && (q.trim().length > 0);

  // Visual variants
  const inputCls = variant === 'hero'
    ? 'pl-10 pr-9 h-12 bg-white/10 border-white/10 text-white placeholder:text-gray-400 text-sm rounded-xl focus:border-[#1A8FE3] focus:ring-[#1A8FE3]/30 backdrop-blur-sm'
    : 'pl-9 pr-9 h-10 text-sm';

  const iconCls = variant === 'hero'
    ? 'absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400'
    : 'absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground';

  const clearCls = variant === 'hero'
    ? 'absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white'
    : 'absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground';

  return (
    <div className="relative" ref={wrapRef} data-testid={`${testidPrefix}-wrap`}>
      <div className="relative">
        <Search className={iconCls} aria-hidden="true" />
        <Input
          ref={inputRef}
          value={q}
          onChange={e => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className={inputCls}
          data-testid={`${testidPrefix}-input`}
          aria-label="Search calculators"
          aria-autocomplete="list"
          aria-expanded={showDropdown}
          autoComplete="off"
          autoFocus={autoFocus}
        />
        {q && (
          <button
            type="button"
            onClick={() => { setQ(''); inputRef.current?.focus(); }}
            className={clearCls}
            aria-label="Clear search"
            data-testid={`${testidPrefix}-clear`}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {showDropdown && results.length > 0 && (
        <div
          className="absolute top-full left-0 right-0 mt-1.5 bg-popover border border-border rounded-xl shadow-2xl overflow-hidden z-50"
          role="listbox"
          data-testid={`${testidPrefix}-results`}
        >
          {results.map((r, i) => (
            <button
              key={r.id}
              role="option"
              aria-selected={i === activeIdx}
              onMouseEnter={() => setActiveIdx(i)}
              onClick={() => go(r)}
              className={`w-full text-left px-4 py-2.5 transition-colors flex items-center justify-between border-b border-border/40 last:border-b-0 ${
                i === activeIdx ? 'bg-[#1A8FE3]/10' : 'hover:bg-muted/50'
              }`}
              data-testid={`${testidPrefix}-result-${i}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-base shrink-0" aria-hidden="true">{r.icon}</span>
                <div className="min-w-0">
                  <div className={`text-sm font-medium truncate ${i === activeIdx ? 'text-[#1A8FE3]' : 'text-foreground'}`}>
                    {r.name}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {r.kind === 'conversion'
                      ? <span><span className="font-jetbrains">{r.preview}</span> · {r.category}</span>
                      : (r.categoryName || r.category || '')}
                  </div>
                </div>
              </div>
              <ArrowRight className="w-3.5 h-3.5 text-muted-foreground shrink-0 ml-2" />
            </button>
          ))}
        </div>
      )}

      {showDropdown && results.length === 0 && (
        <div
          className="absolute top-full left-0 right-0 mt-1.5 bg-popover border border-border rounded-xl shadow-2xl px-4 py-3 z-50 text-sm text-muted-foreground"
          data-testid={`${testidPrefix}-empty`}
        >
          No results for <span className="text-foreground font-medium">"{q}"</span> — try browsing categories below.
        </div>
      )}
    </div>
  );
}

// ─── QuickChips — clickable example queries ──────────────────────────────────
import { QUICK_CHIPS } from '@/data/searchDictionary';

export function QuickChips({ chips = QUICK_CHIPS, testidPrefix = 'quick-chip' }) {
  const fire = (text) => {
    window.dispatchEvent(new CustomEvent('calcglobe:fire-search', { detail: { query: text } }));
  };
  return (
    <div
      className="flex flex-wrap gap-2"
      data-testid="quick-chips"
    >
      {chips.map(chip => (
        <button
          key={chip}
          type="button"
          onClick={() => fire(chip)}
          className="shrink-0 px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap bg-muted/60 text-[#1A8FE3] border border-transparent hover:bg-[#1A8FE3] hover:text-white hover:border-[#1A8FE3] transition-all"
          data-testid={`${testidPrefix}-${chip.replace(/\s+/g, '-')}`}
        >
          {chip}
        </button>
      ))}
    </div>
  );
}
